<template>
  <div class="text-base">
    <base-button
      v-if="standalone !== 'true' && hideButton !== 'true'"
      @click="startTest"
      class="hover:bg-black hover:text-white start-button"
      >{{ entryButtonText || 'Online Hauttest starten' }}</base-button
    >
    <div
      v-if="openModal"
      ref="scrollContainer"
      class="skin-test fixed top-0 left-0 right-0 bottom-0 bg-white overflow-y-auto"
    >
      <div v-if="loading" class="min-h-screen grid place-content-center"><span class="text-xl">Loading...</span></div>
      <div v-else class="min-h-screen flex flex-col">
        <div class="w-full bg-white z-20 fixed md:sticky header">
          <AppHeader
            class="px-6 shadow-lg bg-white"
            :isResultView="isResultView"
            :showProgress="showProgress"
            @restartQuiz="onRestartQuiz"
          />
        </div>
        <div class="flex-1 flex flex-col main">
          <main class="container pt-22 pb-20 md:py-20">
            <router-view></router-view>
          </main>
        </div>
        <AppFooterResult v-if="isResultView" @restartQuiz="onRestartQuiz" />
        <AppFooter v-else class="fixed md:static w-full bg-white z-10 bottom-0" />
      </div>
      <div id="modal"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FeatureToggleComponent as feature } from 'vue-feature-toggle'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import AppFooterResult from '@/components/layout/AppFooterResult.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useMainStore } from '@/store/main'
import { computed, inject, nextTick, onMounted, provide, ref, watch } from 'vue'
import { testStatus } from './types/test-status'
import { RouteLocationNormalized, RouteLocationNormalizedLoaded, useRoute, useRouter } from 'vue-router'

const props = defineProps({
  appUrl: String,
  entryButtonText: String,
  standalone: String,
  hideButton: String,
})
// glolal method imports
/**
 * register store
 */
const store = useMainStore()

/**
 * inject local config
 */
const config: any = inject('config')

/**
 * register router
 */
const router = useRouter()

/**
 * get route properties
 */
const route = useRoute()

// internal refs
const loading = ref(true)

/* Computed props */

/**
 * @retruns  {Boolean} isResultView
 * check is router of result view or finish and
 */

const isResultView = computed(() => route.name == 'result')

/**
 * @returs {Boolean} show not header on finish view
 */
const showProgress = computed(() => !(route.name == 'ambiguous'))

// Store inits

/**
 * @return {Language} textSnippets
 */
const textSnippets = computed(() => store.snippets)

/**
 * @returns {number} page
 */
const page = computed(() => store.page)
/**
 * @returns {number} pages
 */
const pages = computed(() => store.pages)

/**
 * @returns {string|number} id
 */
const id = computed(() => route.params.id)

// template refs
/**
 * get scroll contsainer template ref
 */
const scrollContainer = ref<HTMLElement | null>(null)

// Methods
/**
 * reset Test
 */
const onRestartQuiz = () => {
  store.resetAnswers()
  store.resetCart()
  store.resetResult()
  startTest()
}

const closing = ref(false)

const closeModal = () => {
  closing.value = true
  if (window.history.length <= 2) {
    router.replace({ name: 'home' })
  } else {
    router.back()
  }
}

router.afterEach((to, from) => {
  if (closing.value && isActive(to)) {
    nextTick(() => {
      closeModal()
    })
    return
  }
  closing.value = false
})

const isActive = (route: RouteLocationNormalized | RouteLocationNormalizedLoaded): boolean => {
  return route.name === 'start' || route.name === 'ambiguous' || route.name === 'result'
}

/**
 * @property {boolean} openModal
 * @description reactiv prop to toggle modal
 */
const openModal = computed(() => isActive(route))

/**
 * @param {boolean} openState
 * @description function to toggle the openState of the modal
 * init the test and go to first test page
 */
const toggleModal = async function () {
  reset()
  closeModal()
}

/**
 * set test status
 * request API
 * set global Store
 */

const isLoading = (isLoading: boolean): void => {
  loading.value = isLoading
}

const startTest = async function () {
  await router.push({ name: 'start', params: { id: '1' } })
}

const onTestStarted = async function () {
  isLoading(true)

  store.setStatus(testStatus.Started)
  await store.init(config)
  // feature control
  if (config.features) {
    feature.visibility('save-result-to-shop-account', config.features['save-result-to-shop-account'] ?? false)
    feature.visibility('disable-cart', !(config.features['cart-disabled'] ?? false))
    feature.visibility('promo-popup', config.features['promo-popup'] ?? false)
    feature.visibility(
      'send-participant-information-to-shop',
      config.features['send-participant-information-to-shop'] ?? false,
    )
  }

  /**
   * set Meta for Langauage
   */
  setMeta()

  isLoading(false)
}

// start test onmounted
onMounted(() => {
  if (document) store.setReferrer(document.referrer)
  if (config?.standalone === 'true' && !isActive(route)) {
    router.replace({ name: 'start', params: { id: '1' } })
  }
})

/**
 * @param {boolean} value
 */
const bodyOverflow = (value: boolean): void => {
  if (import.meta.env.MODE === 'production') {
    if (!value) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }
}

/**
 * @param {boolean} value
 */
const loadGlobalCss = (value: boolean): void => {
  if (import.meta.env.MODE === 'production') {
    const skinTestCss = document.getElementById('skinTestCss') as Element

    if (!value) {
      for (let i = 0; i < document.styleSheets.length; i++) {
        const styleItem = document.styleSheets.item(i)

        if (styleItem) {
          const ownerNode = styleItem.ownerNode as Element
          if (ownerNode.id !== 'skinTestCss') styleItem.disabled = true
        }
      }
    } else {
      for (let i = 0; i < document.styleSheets.length; i++) {
        const styleItem = document.styleSheets.item(i)

        if (styleItem) {
          const ownerNode = styleItem.ownerNode as Element
          if (ownerNode.id !== 'skinTestCss') styleItem.disabled = false
        }
      }
    }
  }
}
/**
 * remove the hash navigation
 */
const removeHash = (): void => {
  history.replaceState(history.state, '', window.location.pathname + window.location.search)
  // history.pushState('', document.title, window.location.pathname + window.location.search)
}
/**
 * reset state of app in store
 */
const reset = (): void => {
  store.resetAnswers()
  store.resetCart()
  store.resetResult()
}
// set lang meta
const setMeta = (): void => {
  document.documentElement.setAttribute('lang', textSnippets.value.locale)
}

/**
 * Watcher
 */
/**
 * @description watch router param and set store page
 */
watch(
  id,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      const page: number = +newValue
      store.setPage(page)
      const lastAnswer = store.getLastAnsweredQuestion
      if (page > lastAnswer + 1) {
        nextTick(() => {
          router.replace({ name: 'start', params: { id: lastAnswer + 1 } })
        })
      }
    }
  },
  {
    immediate: true,
  },
)

/**
 * @description watch for open modal to set body overflow
 */
watch(
  openModal,
  (newValue, oldValue) => {
    bodyOverflow(!newValue)
    loadGlobalCss(!newValue)
    if (!newValue) {
      removeHash()
    } else if (!oldValue) {
      onTestStarted()
    }
  },
  {
    immediate: true,
  },
)

/**
 * router guards
 */
router.beforeEach((to, from) => {
  if (!to.name) {
    loading.value = true
  }
  scrollContainer?.value?.scroll(0, 0)
})

// Provider for dependecy injection

/**
 * @description provide toggleModal function for global inject
 */
provide('toggleModal', toggleModal)

/**
 * @description provide loading overlay function for global inject
 */
provide('isLoading', isLoading)
/**
 * @description provides restart quiz function for global inject
 */
provide('restartQuiz', onRestartQuiz)
</script>
<style scoped>
.skin-test {
  z-index: 1100;
}
@screen md {
  .header {
    top: -77px;
  }
}
</style>
